import { LinkIcon } from '@chakra-ui/icons';
import {
	Button,
	Flex,
	Heading,
	Stack,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	AttachMoney,
	Link as LinkOutlined,
	ReceiptLong,
	SyncAltOutlined,
	TrendingDown,
	TrendingUp,
} from '@mui/icons-material';
import { balanceHistoryService } from 'api/services/balanceHistoryService';
import { conversionService } from 'api/services/conversionService';
import { linkService } from 'api/services/linkService';
import { transactionService } from 'api/services/transactionService';
import ListingCard from 'components/listing/ListingCard';
import PaginateControls from 'components/listing/PaginateControls';
import { LoadingSpinner } from 'components/loading/LoadingSpinner';
import ResultMessage from 'components/modal/ResultMessage';
import IconTabButtons from 'components/tabs/IconTabButtons';
import { ALERT } from 'constant/messages';
import { NAVIGATE } from 'constant/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { storeActions } from 'store/store';
import { displayNumericString, formatDataType, getImage } from 'utils/format';
import { fetchData, handleCloseModal } from 'utils/hooks';

export default function Activity() {
	const cardBg = useColorModeValue('gray.100', 'gray.500');
	const iconBg = useColorModeValue('gray.500', 'gray.500');
	const TOKEN = useSelector((state) => state.auth.tokenId);

	const {
		storeAllLink,
		storeAllConversion,
		storeAllTransaction,
		storeAllBalanceHistory,
	} = useSelector((state) => state.store);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const activeTabInitialState = location.state?.activeTab || 'conversiones';

	const ITEMS_PER_PAGE = 10;
	const [currentPage, setCurrentPage] = useState(1);
	const [conversionData, setConversionData] = useState(
		storeAllConversion || []
	);
	const [transactionData, setTransactionData] = useState(
		storeAllTransaction || []
	);
	const [linkData, setLinkData] = useState(storeAllLink || []);
	const [balanceHistoryData, setBalanceHistoryData] = useState(
		storeAllBalanceHistory || []
	);
	const [activeTab, setActiveTab] = useState(activeTabInitialState);

	const hasNoLocalData =
		!storeAllConversion?.length &&
		!storeAllLink?.length &&
		!storeAllTransaction?.length &&
		!storeAllBalanceHistory?.length;
	const [loading, setLoading] = useState(hasNoLocalData);

	const [message, setMessage] = useState({
		state: false,
		title: '',
		subtitle: '',
	});

	useEffect(() => {
		document.querySelector('.scroll-body')?.scrollTo(0, 0);
		fetchData({
			apiCalls: [
				() => conversionService.getAll(TOKEN),
				() => transactionService.getAll(TOKEN),
				() => linkService.getAll(TOKEN),
				() => balanceHistoryService.getAll(TOKEN),
			],
			setStates: [
				setConversionData,
				setTransactionData,
				setLinkData,
				setBalanceHistoryData,
			],
			setStores: [
				(data) =>
					dispatch(storeActions.setStoreData({ storeAllConversion: data })),
				(data) =>
					dispatch(storeActions.setStoreData({ storeAllTransaction: data })),
				(data) => dispatch(storeActions.setStoreData({ storeAllLink: data })),
				(data) =>
					dispatch(storeActions.setStoreData({ storeAllBalanceHistory: data })),
			],
			alertMessages: ALERT,
			setMessage,
			setLoading,
		});
	}, [activeTabInitialState]);

	useEffect(() => setCurrentPage(1), [activeTab]);

	const getCardProps = (tx) => {
		switch (activeTab) {
			case 'conversiones':
				return {
					imageSrc: getImage(tx._brandId?.image, 'logo'),
					title: tx._brandId?.brandName,
					subtitle: formatDataType('date', tx.createdAt),
					borderColor: `${tx.status === 'approved' ? 'green' : 'yellow'}.400`,
					badge: {
						label: formatDataType('status', tx.status),
						colorScheme: tx.status === 'approved' ? 'green' : 'yellow',
					},
					details: [
						{
							label: 'Monto',
							value: `${displayNumericString(tx.networkAmountSale)} ${
								tx.currencyFiat
							}`,
						},
						{
							label: 'Cashback',
							value: `${displayNumericString(
								tx.bumbeiAmountCashback,
								true
							)} sats (${tx.networkRateCommission}%)`,
						},
						{
							label: 'Tasa BTC',
							value: `${displayNumericString(
								tx.currencyRateExchange,
								true
							)} sats / ${tx.currencyFiat}`,
						},
					],
				};
			case 'enlaces':
				return {
					imageSrc: getImage(tx._brandId?.image, 'logo'),
					iconComponent: LinkIcon,
					title: tx._brandId?.brandName,
					subtitle: formatDataType('date', tx.createdAt),
					borderColor: `${tx.status === 'activo' ? 'green' : 'yellow'}.400`,
					badge: {
						label: formatDataType('status', tx.status),
						colorScheme: tx.status === 'activo' ? 'green' : 'yellow',
					},
					details: [
						{ label: 'Enlace', value: tx.shortLink, type: 'copyable_chip' },
						...(tx.campaign ? [{ label: 'Campaña', value: tx.campaign }] : []),
						...(tx.tracker ? [{ label: 'Tracker', value: tx.tracker }] : []),
					],
				};
			case 'historial':
				return {
					iconComponent: tx.type === 'withdrawal' ? TrendingDown : TrendingUp,
					title:
						tx.type === 'withdrawal'
							? `- ${displayNumericString(tx.amount, true)} ${tx.currency}`
							: `+ ${displayNumericString(tx.amount, true)} ${tx.currency}`,
					subtitle: formatDataType('date', tx.createdAt),
					borderColor: `${tx.type === 'withdrawal' ? 'yellow' : 'green'}.400`,
					badge: {
						label: formatDataType('status', tx.sourceType),
						colorScheme: tx.type === 'withdrawal' ? 'yellow' : 'green',
					},
					details: [
						{
							label: 'Balance total anterior',
							value: `${displayNumericString(
								tx.balanceTotalBefore,
								true
							)} SATS`,
						},
						{
							label: 'Balance total actualizado',
							value: `${displayNumericString(tx.balanceTotalAfter, true)} SATS`,
						},
					],
				};
			default:
				return {
					iconComponent: SyncAltOutlined,
					title: `-${displayNumericString(tx.amount, true)} ${
						tx.currency || 'SATS'
					}`,
					subtitle: formatDataType('date', tx.createdAt),
					borderColor: 'green.400',
					badge: {
						label: formatDataType('status', tx.status),
						colorScheme: 'green',
					},
				};
		}
	};

	const paginatedData = useMemo(() => {
		const dataMap = {
			conversiones: conversionData,
			enlaces: linkData,
			retiros: transactionData,
			historial: balanceHistoryData,
		};
		return dataMap[activeTab].slice(
			(currentPage - 1) * ITEMS_PER_PAGE,
			currentPage * ITEMS_PER_PAGE
		);
	}, [
		activeTab,
		conversionData,
		linkData,
		transactionData,
		balanceHistoryData,
		currentPage,
	]);

	const totalPages = Math.ceil(
		(activeTab === 'conversiones'
			? conversionData.length
			: activeTab === 'enlaces'
			? linkData.length
			: activeTab === 'historial'
			? balanceHistoryData.length
			: transactionData.length) / ITEMS_PER_PAGE
	);

	const isEmpty = () => {
		if (activeTab === 'conversiones') return conversionData.length === 0;
		if (activeTab === 'enlaces') return linkData.length === 0;
		if (activeTab === 'retiros') return transactionData.length === 0;
		if (activeTab === 'historial') return balanceHistoryData.length === 0;
		return true;
	};

	const emptyMessage = {
		conversiones:
			'Aún no has generado conversiones. Explora y genera actividad.',
		enlaces: 'Aquí podrás encontrar todos los enlaces que generes con Bumbei.',
		retiros: 'Aún no has hecho retiros. Recibirás tus SATs aquí.',
		historial:
			'Tu historial de balance aparecerá aquí cuando haya movimientos.',
	};

	const tabs = [
		{
			label: 'conversiones',
			icon: AttachMoney,
			onClick: () => setActiveTab('conversiones'),
			isActive: activeTab === 'conversiones',
		},
		{
			label: 'enlaces',
			icon: LinkOutlined,
			onClick: () => setActiveTab('enlaces'),
			isActive: activeTab === 'enlaces',
		},
		{
			label: 'retiros',
			icon: SyncAltOutlined,
			onClick: () => setActiveTab('retiros'),
			isActive: activeTab === 'retiros',
		},
		{
			label: 'historial',
			icon: ReceiptLong,
			onClick: () => setActiveTab('historial'),
			isActive: activeTab === 'historial',
		},
	];

	return (
		<Flex direction='column' pt={{ sm: '125px', lg: '75px' }}>
			<Heading mb='20px' fontSize='2xl' ms='24px' fontWeight='700'>
				Tu actividad
			</Heading>
			<LoadingSpinner loading={loading} />
			<Stack spacing={5}>
				{!loading && (
					<IconTabButtons tabs={tabs} activeTab={activeTab} layout={'list'} />
				)}
				{!loading && isEmpty() && (
					<Flex
						flexDirection='column'
						p='3rem'
						justifyContent='center'
						alignItems='center'
					>
						<Text textAlign='center'>{emptyMessage[activeTab]}</Text>
						<Button
							variant='brand'
							mt='25px'
							onClick={() => navigate(NAVIGATE.BRANDS)}
						>
							Explorar marcas
						</Button>
					</Flex>
				)}
				{!loading && !isEmpty() && (
					<>
						<Flex
							direction='column'
							overflowY='auto'
							maxH='60vh'
							minH='60vh'
							gap={5}
							px={6}
						>
							{paginatedData.map((tx) => (
								<ListingCard
									key={tx.id}
									idx={tx.id}
									cardBg={cardBg}
									iconBg={iconBg}
									{...getCardProps(tx)}
								/>
							))}
						</Flex>
						<PaginateControls
							setCurrentPage={setCurrentPage}
							currentPage={currentPage}
							totalPages={totalPages}
						/>
					</>
				)}
			</Stack>
			<ResultMessage
				isOpen={message.state}
				onClose={() => handleCloseModal(setMessage)}
				message={message}
			/>
		</Flex>
	);
}
